import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  CircularProgress,
  Card,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Person,
  CalendarToday,
  Delete,
  Info,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../hooks/useAuth';
// Using built-in Date methods instead of date-fns

interface DeletedClient {
  _id: string;
  name: string;
  email: string;
  trackingCode: string;
  phoneNumber: string;
  status: string;
  deletedAt: string;
  deletionReason: string;
  deletedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
}

export default function DeletedClientsPage() {
  const [deletedClients, setDeletedClients] = useState<DeletedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState<DeletedClient | null>(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchDeletedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await api.get(`/clients/deleted/list?${params}`);
      
      if (response.data.success) {
        setDeletedClients(response.data.data);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err: any) {
      console.error('Error fetching deleted clients:', err);
      setError(err.response?.data?.message || 'Erro ao carregar clientes deletados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchDeletedClients();
    } else {
      navigate('/dashboard');
    }
  }, [page, searchTerm, user]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1); // Reset to first page when searching
  };

  const handleViewDetails = (client: DeletedClient) => {
    setSelectedClient(client);
    setDetailsDialog(true);
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: '#ff9800',
      approved: '#4caf50',
      rejected: '#f44336',
      under_review: '#2196f3',
      completed: '#4caf50',
    };
    return colors[status] || '#757575';
  };

  const getStatusLabel = (status: string) => {
    const labels: { [key: string]: string } = {
      pending: 'Pendente',
      approved: 'Aprovado',
      rejected: 'Rejeitado',
      under_review: 'Em Análise',
      completed: 'Concluído',
    };
    return labels[status] || status;
  };

  if (user?.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Acesso negado. Apenas administradores podem visualizar esta página.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Clientes Deletados
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Histórico de clientes deletados com informações de auditoria
          </Typography>
        </Box>

        {/* Search and Actions */}
        <Card sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Buscar por nome, código, email ou motivo..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchDeletedClients}
              disabled={loading}
            >
              Atualizar
            </Button>
          </Stack>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Deletado Por</TableCell>
                  <TableCell>Data de Exclusão</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : deletedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        Nenhum cliente deletado encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedClients.map((client) => (
                    <TableRow key={client._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {client.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {client.trackingCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(client.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(client.status),
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {client.deletedBy.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {client.deletedBy.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(client.deletedAt).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={client.deletionReason} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {client.deletionReason}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver Detalhes">
                          <IconButton
                            onClick={() => handleViewDetails(client)}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              },
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              variant="outlined"
            >
              Anterior
            </Button>
            <Typography sx={{ alignSelf: 'center', px: 2 }}>
              Página {page} de {totalPages}
            </Typography>
            <Button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              variant="outlined"
            >
              Próxima
            </Button>
          </Box>
        )}
      </Stack>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info color="primary" />
            <Typography variant="h6">Detalhes do Cliente Deletado</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Stack spacing={3}>
              {/* Client Info */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Informações do Cliente
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Nome:
                    </Typography>
                    <Typography variant="body2">{selectedClient.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Email:
                    </Typography>
                    <Typography variant="body2">{selectedClient.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Código:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedClient.trackingCode}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Telefone:
                    </Typography>
                    <Typography variant="body2">{selectedClient.phoneNumber}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Status:
                    </Typography>
                    <Chip
                      label={getStatusLabel(selectedClient.status)}
                      size="small"
                      sx={{
                        backgroundColor: getStatusColor(selectedClient.status),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Deletion Info */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                  Informações de Exclusão
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Deletado por:
                    </Typography>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedClient.deletedBy.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedClient.deletedBy.email}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Data:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedClient.deletedAt).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Motivo:
                    </Typography>
                    <Typography variant="body2">{selectedClient.deletionReason}</Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Timeline */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                  Timeline
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Criado em:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedClient.createdAt).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100 }}>
                      Deletado em:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedClient.deletedAt).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
